/**
 * Browser media helpers for Interview Modes and plan rounds (voice / video).
 * Feature detection first, soft constraints, and mime fallbacks:
 * Chrome/Edge/Firefox record WebM (VP8/VP9 + Opus); Safari records MP4.
 */

const AUDIO_MIMES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus', 'audio/mp4', 'audio/aac'];
const VIDEO_MIMES = ['video/webm;codecs=vp8,opus', 'video/webm;codecs=vp9,opus', 'video/webm', 'video/mp4'];

const win = () => (typeof window === 'undefined' ? null : window);

export function mediaDevicesAvailable() {
  return Boolean(
    typeof navigator !== 'undefined'
    && navigator.mediaDevices
    && typeof navigator.mediaDevices.getUserMedia === 'function'
  );
}

export function secureContext() {
  const w = win();
  if (!w) return true;
  if (typeof w.isSecureContext === 'boolean') return w.isSecureContext;
  const { protocol = '', hostname = '' } = w.location || {};
  return protocol === 'https:' || hostname === 'localhost' || hostname === '127.0.0.1';
}

const httpsHint = () => {
  const { host = '', pathname = '' } = win()?.location || {};
  return host ? ` Open https://${host}${pathname} instead.` : '';
};

/** {ok, code, message}: can this page use the camera/microphone and record at all? */
export function mediaSupport() {
  if (!secureContext()) {
    return {
      ok: false,
      code: 'INSECURE_CONTEXT',
      message: `Camera and microphone only work on a secure (https://) page. This page was opened over http://, so the browser hides them.${httpsHint()}`,
    };
  }
  if (!mediaDevicesAvailable()) {
    return {
      ok: false,
      code: 'NO_MEDIA_DEVICES',
      message: 'This browser does not give websites camera or microphone access. Use a recent Chrome, Edge, Firefox or Safari.',
    };
  }
  const w = win();
  if (!w || typeof w.MediaRecorder === 'undefined') {
    return {
      ok: false,
      code: 'NO_RECORDER',
      message: 'This browser cannot record audio or video. Use a recent Chrome, Edge, Firefox or Safari (14.1+).',
    };
  }
  return { ok: true, code: '', message: '' };
}

const supportError = (support) => {
  const err = new Error(support.message);
  err.code = support.code;
  return err;
};

export function pickRecorderMime(kinds = ['audio'], isSupported) {
  const MR = win()?.MediaRecorder;
  const check = isSupported
    || (MR && typeof MR.isTypeSupported === 'function' ? (type) => MR.isTypeSupported(type) : null);
  if (!check) return '';
  const list = kinds.includes('video') ? VIDEO_MIMES : AUDIO_MIMES;
  return list.find((type) => check(type)) || '';
}

const RETRYABLE = new Set(['OverconstrainedError', 'ConstraintNotSatisfiedError', 'NotReadableError', 'AbortError', 'TypeError']);

/** getUserMedia with the given constraints, then once more with bare {audio, video} flags. */
export async function requestUserMedia(constraints, { getUserMedia } = {}) {
  const get = getUserMedia || ((c) => navigator.mediaDevices.getUserMedia(c));
  if (!getUserMedia) {
    const support = mediaSupport();
    if (!support.ok && support.code !== 'NO_RECORDER') throw supportError(support);
  }
  try {
    return await get(constraints);
  } catch (first) {
    if (!RETRYABLE.has(first?.name)) throw first;
    try {
      return await get({ audio: Boolean(constraints.audio), video: Boolean(constraints.video) });
    } catch {
      throw first;
    }
  }
}

export async function getSoftUserMedia({ video = false } = {}) {
  return requestUserMedia(
    video
      ? { video: { facingMode: 'user' }, audio: { echoCancellation: true, noiseSuppression: true } }
      : { audio: { echoCancellation: true, noiseSuppression: true } },
  );
}

export function createMediaRecorder(stream, kinds = ['audio']) {
  const mimeType = pickRecorderMime(kinds);
  let recorder;
  try {
    recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
  } catch {
    recorder = new MediaRecorder(stream);
  }
  return {
    recorder,
    mimeType: recorder.mimeType || mimeType || (kinds.includes('video') ? 'video/webm' : 'audio/webm'),
  };
}

export const fileExtensionFor = (mimeType = '') => {
  const type = String(mimeType).toLowerCase();
  if (type.includes('mp4') || type.includes('aac')) return 'mp4';
  if (type.includes('ogg')) return 'ogg';
  return 'webm';
};

/**
 * Records the stream's audio as standalone clips of `segmentMs`, restarting the recorder for each,
 * so every clip has its own container header and can be transcribed on its own.
 * (Timeslice parts of one recording cannot: only the first carries the header.)
 */
export function createSegmentRecorder(stream, { segmentMs = 20000, onSegment, Recorder } = {}) {
  const MR = Recorder || win()?.MediaRecorder;
  const tracks = stream?.getAudioTracks?.() || [];
  if (!MR || !tracks.length) return { start() {}, stop: async () => {}, active: false };
  const audioOnly = typeof MediaStream !== 'undefined' ? new MediaStream(tracks) : stream;
  const mimeType = pickRecorderMime(['audio'], MR.isTypeSupported ? (t) => MR.isTypeSupported(t) : undefined);
  let index = 0;
  let current = null;
  let timer = null;
  let running = false;
  let lastStop = Promise.resolve();
  const pending = [];

  const startOne = () => {
    const parts = [];
    let rec;
    try {
      rec = mimeType ? new MR(audioOnly, { mimeType }) : new MR(audioOnly);
    } catch {
      rec = new MR(audioOnly);
    }
    const stopped = new Promise((resolve) => {
      rec.ondataavailable = (event) => {
        if (event.data && event.data.size) parts.push(event.data);
      };
      rec.onstop = () => {
        const type = rec.mimeType || mimeType || 'audio/webm';
        if (parts.length) {
          const segment = index;
          index += 1;
          const result = onSegment?.({ index: segment, blob: new Blob(parts, { type }), mimeType: type });
          if (result && typeof result.then === 'function') pending.push(result.catch(() => {}));
        }
        resolve();
      };
    });
    rec.start();
    current = { rec, stopped };
  };

  const cycle = () => {
    if (!running || !current) return;
    const { rec, stopped } = current;
    if (rec.state !== 'inactive') rec.stop();
    lastStop = stopped;
    startOne();
  };

  return {
    active: true,
    start() {
      if (running) return;
      running = true;
      startOne();
      timer = setInterval(cycle, segmentMs);
    },
    /** Stops recording and resolves once every clip's onSegment promise has settled. */
    async stop() {
      running = false;
      clearInterval(timer);
      if (current) {
        const { rec, stopped } = current;
        current = null;
        if (rec.state !== 'inactive') rec.stop();
        await Promise.all([lastStop, stopped]);
      }
      await Promise.all(pending);
    },
  };
}

/** User-facing text for SpeechRecognition error codes; '' when nothing needs saying. */
export function speechRecognitionErrorMessage(code) {
  switch (code) {
    case 'not-allowed':
    case 'service-not-allowed':
      return secureContext()
        ? 'Voice input is blocked. Allow the microphone for this site in the address bar, or type instead.'
        : 'Voice input needs a secure (https://) page. Type instead, or open the https:// address.';
    case 'audio-capture':
      return 'No microphone was found for voice input. Type instead.';
    case 'network':
      return "This browser's speech service could not be reached, so voice input is off. Type instead.";
    case 'language-not-supported':
      return 'Voice input does not support your browser language. Type instead.';
    default:
      return '';
  }
}

export function mediaPermissionMessage(err) {
  const name = err?.name || '';
  if (['INSECURE_CONTEXT', 'NO_MEDIA_DEVICES', 'NO_RECORDER'].includes(err?.code)) {
    return err.message;
  }
  if (name === 'NotAllowedError' || name === 'PermissionDeniedError' || name === 'SecurityError') {
    return 'Camera/microphone access is blocked. Click the camera icon in the address bar (or the lock icon → Site settings), choose Allow, then tap Retry. On Windows, also check Settings → Privacy → Camera / Microphone.';
  }
  if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
    return 'No camera or microphone was found. Plug one in (or enable it), then tap Retry.';
  }
  if (name === 'NotReadableError' || name === 'TrackStartError') {
    return 'Camera or microphone is in use by another app (Zoom, Teams, another tab). Close it and tap Retry.';
  }
  if (name === 'OverconstrainedError' || name === 'ConstraintNotSatisfiedError') {
    return 'This device cannot use the requested camera settings. Tap Retry for a simpler setup.';
  }
  if (name === 'AbortError') {
    return 'The browser stopped the camera/microphone from starting. Tap Retry.';
  }
  return err?.message || 'Could not access camera/microphone. Check permissions and try again.';
}
